/**
 * useInboxAuditDetails - Resumo + timeline de movimentações de uma inbox.
 *
 * ARQUITETURA:
 * - 100% server-side via RPC `get_inbox_audit_details_safe`
 * - Retorna { summary, timeline } (sem credenciais/tokens)
 * - Padrão Tenant v2.0: tenant validado no backend (p_tenant_hint opcional)
 *
 * RELEASE:
 *   2026-08-08 v1.0.0 - Versão inicial (modal de detalhes da inbox).
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import type { AuditTimelineEvent } from '@/components/tenant/shared/audit/AuditTimelineItem';

const logger = createLogger('useInboxAuditDetails');

/** Resumo da inbox exibido no bloco "Resumo" */
export interface InboxAuditSummary {
  id: string;
  inbox_code: string;
  name: string;
  channel_type: string;
  identifier: string | null;
  status: string;
  is_default: boolean;
  provider_display_name: string | null;
  provider_slug: string | null;
  group_name: string | null;
  created_at: string;
  updated_at: string;
  instance_name: string | null;
  instance_code: string | null;
  instance_status: string | null;
}

/** Payload completo retornado pela RPC */
export interface InboxAuditDetailsPayload {
  summary: InboxAuditSummary | null;
  timeline: AuditTimelineEvent[];
}

/**
 * Busca resumo e movimentações da inbox (somente com o modal aberto).
 */
export function useInboxAuditDetails(inboxId: string | null | undefined, enabled = true) {
  return useQuery<InboxAuditDetailsPayload>({
    queryKey: ['inbox-audit-details', inboxId],
    enabled: enabled && !!inboxId,
    queryFn: async () => {
      logger.info('Buscando auditoria da inbox', { inboxId });

      const { data, error } = await supabase.rpc('get_inbox_audit_details_safe', {
        p_inbox_id: inboxId!,
      });

      if (error) {
        logger.error('Erro ao buscar auditoria da inbox', { error });
        throw error;
      }

      return (
        (data as unknown as InboxAuditDetailsPayload) ?? { summary: null, timeline: [] }
      );
    },
    // Auditoria muda pouco: evita refetch agressivo ao reabrir o modal
    staleTime: 30 * 1000,
  });
}
